import { Image } from "@/components/image";
import { NoImage } from "@/components/no-image/NoImage";
import { Avatar, Heading, PageContainer, Skeleton, Surface } from "@/components/ui";
import { Storefront } from "@/icons";
import { useStorefrontAppearance } from "@/hooks/use-storefront-appearance";
import { BasicInfo } from "@/types";

/**
 * Indoor digital-menu header — banner + logo + restaurant name. Scrolls with
 * the page (not fixed); the fixed search top bar is MenuTopBar.
 *
 * Banner follows the same home switch (`header.showBanner`): storefront cover,
 * not this screen's decoration — indoor must not show a cover the delivery
 * channel hides.
 */
export function MenuHeader({ basicInfo }: { basicInfo: BasicInfo }) {
  const { header } = useStorefrontAppearance();
  const logoUrl = basicInfo?.logoImage?.URL;

  const logo = logoUrl ? (
    <Avatar
      src={logoUrl}
      alt="logo"
      crc={basicInfo?.logoImage?.["CRC-32"]}
      size="2xl"
      surface="card"
      className="shrink-0"
    />
  ) : (
    <Avatar alt="logo" icon={Storefront} size="2xl" surface="card" className="shrink-0" />
  );

  const name = (
    <div className="flex w-full items-end justify-between">
      <Heading level={1} size="title" truncate className="min-w-0">
        {basicInfo.name}
      </Heading>
    </div>
  );

  return (
    <div className="pt-header overflow-hidden">
      {header.showBanner && (
        <div className="overflow-hidden aspect-banner w-full">
          <div className="fixed -z-raised aspect-banner w-full max-w-shell m-auto overflow-hidden">
            <Skeleton shape="flat" className="absolute inset-0" />
            {basicInfo?.bannerImage?.URL ? (
              <Image
                width={1200}
                height={400}
                alt="banner"
                className="relative w-full h-full object-cover bg-card"
                src={basicInfo.bannerImage.URL}
                crc={basicInfo.bannerImage["CRC-32"]}
                priority
                sizes="(max-width: 1024px) 100vw, 1024px"
              />
            ) : (
              <div className="relative flex items-center justify-center w-full h-full">
                <NoImage width={300} height={212} className="border-none w-full h-full" />
              </div>
            )}
          </div>
        </div>
      )}

      {header.showBanner ? (
        <>
          <PageContainer className="-mt-14 flex">{logo}</PageContainer>

          <Surface surface="card" className="-mt-14 pb-4 pt-12">
            <PageContainer>{name}</PageContainer>
          </Surface>
        </>
      ) : (
        <Surface surface="card" className="py-4">
          <PageContainer className="flex flex-col items-center sm:flex-row sm:items-center sm:gap-6">
            {logo}
            <div className="w-full min-w-0 pt-2 sm:flex-1 sm:pt-0">{name}</div>
          </PageContainer>
        </Surface>
      )}
    </div>
  );
}
